import './bootstrap'
import '../css/app.css'
import 'primeicons/primeicons.css'

import { createInertiaApp } from '@inertiajs/vue3'
import messages from '@intlify/unplugin-vue-i18n/messages'
import { definePreset } from '@primeuix/themes'
import Aura from '@primeuix/themes/aura'
import { format, parse, parseISO } from 'date-fns'
import { createPinia } from 'pinia'
import PrimeVue, { usePrimeVue } from 'primevue/config'
import ConfirmationService from 'primevue/confirmationservice'
import FocusTrap from 'primevue/focustrap'
import ToastService from 'primevue/toastservice'
import Tooltip from 'primevue/tooltip'
import { createApp, h } from 'vue'
import { createI18n } from 'vue-i18n'
import { ZiggyVue } from '../../vendor/tightenco/ziggy'
import locales from './primelocales'
import { patchWmsLayers } from './services/wmsLayerPatcher'
import resetStore from './stores/resetStore'

const Redata = definePreset(Aura, {
  primitive: {},
  semantic: {
    primary: {
      50: '{blue.50}',
      100: '{blue.100}',
      200: '{blue.200}',
      300: '{blue.300}',
      400: '{blue.400}',
      500: '{blue.500}',
      600: '{blue.600}',
      700: '{blue.700}',
      800: '{blue.800}',
      900: '{blue.900}',
      950: '{blue.950}',
    },
  },
  components: {
    multiselect: {
      padding: {
        x: '10px',
        y: '6px',
      },
    },
    floatlabel: {
      over: {
        active: {
          top: '-0.85rem',
        },
      },
    },
    // House tooltip: primary background, contrast text. Setting the tokens
    // (not a pt style) keeps the arrow the right colour in all four
    // positions — the theme derives every arrow border from tooltip.background.
    tooltip: {
      colorScheme: {
        light: {
          root: {
            background: '{primary.color}',
            color: '{primary.contrast.color}',
          },
        },
        dark: {
          root: {
            background: '{primary.color}',
            color: '{primary.contrast.color}',
          },
        },
      },
    },
  },
})

const i18n = createI18n({
  locale: 'et',
  fallbackLocale: 'en',
  messages,
  legacy: false,
})

const pinia = createPinia()
pinia.use(resetStore)

createInertiaApp({
  resolve: name => {
    const pages = import.meta.glob('./Pages/**/*.vue', { eager: true })
    return pages[`./Pages/${name}.vue`]
  },
  setup({ el, App, props, plugin }) {
    // Patch WMS layer versions (only present on Detail page)
    if (props.initialPage.props.wmsLayerVersions) {
      patchWmsLayers(props.initialPage.props.wmsLayerVersions)
    }

    const app = createApp({
      render: () => h(App, props),
      setup() {
        const primevue = usePrimeVue()

        primevue.config.locale = locales['et']

        return {}
      },
    })

    app.config.globalProperties.$filters = {
      formatDate(value) {
        if (value) {
          const date = parse(value, 'yyyy-MM-dd', new Date())
          return format(date, 'dd.MM.yyyy')
        }
        return value
      },

      formatDateTime(value) {
        if (value) {
          const date = parseISO(value)
          return format(date, 'dd.MM.yyyy HH:mm:SS')
        }
        return value
      },
    }

    app
      .use(plugin)
      .use(i18n)
      .use(ConfirmationService)
      .use(ToastService)
      .use(ZiggyVue)
      .use(PrimeVue, {
        theme: {
          preset: Redata,
          options: {
            cssLayer: {
              name: 'primevue',
              order: 'theme, base, primevue, components, utilities',
            },
            darkModeSelector: false || 'none',
          },
          zIndex: {
            modal: 1100, //dialog, drawer
            overlay: 1000, //select, popover
            menu: 1000, //overlay menus
            tooltip: 1100, //tooltip
          },
        },
        // Global pass-through for the v-tooltip directive (key shape is
        // pt.directives.<name>, unlike components). mergeProps makes a local
        // `pt.text` ADD to this class instead of replacing it.
        pt: {
          directives: {
            tooltip: {
              text: 'text-sm',
            },
          },
        },
        ptOptions: {
          mergeProps: true,
        },
      })
      .directive('tooltip', Tooltip)
      .directive('focustrap', FocusTrap)
      .use(pinia)

    // Lazy load translations
    import(`../lang/et.json`)
      .then(messages => {
        i18n.global.setLocaleMessage('et', messages.default)
        app.mount(el)
      })
      .catch(error => {
        console.error('Failed to load translations:', error)
        app.mount(el)
      })

    return app
  },
})
